import { z } from "zod";
import { IsoDateTime } from "./common.js";
/**
 * `POST /auth/session` (CDC §7.2, §7.3, lot B7.2). Deux régimes d'identité,
 * jamais confondus :
 * - `kind: "post"` — identifiants de **poste/rôle**, partagés par une équipe.
 *   Aucune identité de personne.
 * - `kind: "admin"` — compte d'**administration de plateforme**, nominatif
 *   par exception assumée (CDC §7.3). Provisionné hors-ligne, jamais par un
 *   endpoint HTTP de création (pas de « premier compte » exposé au réseau).
 */
export const PostSessionRequest = z.object({
    kind: z.literal("post"),
    networkId: z.string(),
    post: z.string(),
    secret: z.string(),
});
export const AdminSessionRequest = z.object({
    kind: z.literal("admin"),
    username: z.string(),
    secret: z.string(),
});
export const SessionRequest = z.discriminatedUnion("kind", [
    PostSessionRequest,
    AdminSessionRequest,
]);
export const PostSessionResponse = z.object({
    token: z.string(),
    post: z.string(),
    network: z.string(),
    /** Réservé — aucun modèle de permission fin n'existe encore au-delà de poste/admin. */
    permissions: z.array(z.string()),
    expiresAt: IsoDateTime,
});
export const AdminSessionResponse = z.object({
    token: z.string(),
    username: z.string(),
    permissions: z.array(z.string()),
    expiresAt: IsoDateTime,
});
export const SessionResponse = z.union([PostSessionResponse, AdminSessionResponse]);
/**
 * Porte d'accès applicative (login principal, décision utilisateur 2026-09-09).
 * Distincte de `POST /auth/session` : ici la session vit dans un cookie
 * `sentinelle_session` (HttpOnly, Secure, SameSite=Strict), jamais dans le corps.
 * Tant qu'aucun cookie valide n'est présent, **toute** route `/api/v1/**`
 * répond `401` — sauf `POST /auth/login` et `GET /health`.
 *
 * - `POST /auth/login` — `{ username, password }` → pose le cookie, renvoie
 *   `AppLoginResponse`. Accepte **soit** un accès applicatif (`app_users`),
 *   **soit** un compte d'administration (`admin_accounts`, le même que
 *   `POST /auth/session { kind: "admin" }`) : un seul écran de connexion pour
 *   les deux. `401 unauthenticated` (message identique) que le compte soit
 *   inconnu, révoqué, ou le mot de passe faux.
 * - `POST /auth/logout` — révoque la session, vide le cookie → `204`.
 * - `GET /auth/me` — `AppMeResponse` si le cookie est valide, `401` sinon
 *   (l'écran de connexion l'appelle au démarrage).
 *
 * `role: "admin"` ⇒ le cookie de session ouvre **aussi** `/api/v1/admin/**`
 * (§8) : connecté en admin par cet écran, plus besoin de se ré-authentifier
 * au panneau d'administration. `role: "app"` ⇒ accès en lecture seulement,
 * `/admin/**` reste fermé.
 *
 * Aucune inscription : les accès sont créés hors-ligne — `apps/api/src/bin/user.ts`
 * pour un accès applicatif, `apps/api/src/bin/create-admin.ts` pour un admin.
 * `username` est un **nom de compte** choisi par l'administrateur (ni adresse
 * e-mail, ni personne) — aucune donnée personnelle (CDC §7.3).
 */
export const AppLoginRequest = z.object({
    username: z.string().min(1),
    password: z.string().min(1),
});
/** `"admin"` ⇒ le cookie ouvre aussi `/admin/**` ; `"app"` ⇒ lecture seule. */
export const AppRole = z.enum(["app", "admin"]);
export const AppLoginResponse = z.object({
    username: z.string(),
    role: AppRole,
    expiresAt: IsoDateTime,
});
export const AppMeResponse = z.object({
    username: z.string(),
    role: AppRole,
    /**
     * v1.56 — ce compte a-t-il accès à l'assistant IA (chat branché sur les
     * données réseau, CDC « conseille, n'agit pas ») sur au moins un réseau
     * géré ? Dérivé côté serveur de `config.assistant.allowedAdminUsernames`
     * (jamais exposé au front tel quel) — le front ne code jamais de nom de
     * compte en dur, il réagit à ce booléen. Toujours `false` pour un compte
     * `role: "app"` (poste).
     */
    assistantEnabled: z.boolean(),
});
//# sourceMappingURL=auth.js.map